import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { get } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import onClickOutside from 'ember-click-outside/modifiers/on-click-outside';
import { t } from 'ember-intl';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { gt } from 'ember-truth-helpers';
import { trackedFunction } from 'reactiveweb/function';

import LinkToDetail from 'umg-ember-backend/components/ui/link-to-detail';
import plus from 'umg-ember-backend/helpers/plus';
import { getBem } from 'umg-ember-backend/utils/get-bem';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

import styles from './styles.module.css';

const bem = getBem(styles);

export default class RecordExplorerTableRowCellRelation extends Component {
  // Services
  @service store;

  // Defaults
  blockName = 'c-record-explorer-table-td-relation';
  @tracked showMore = false;

  // Getter and setter
  get isBelongsTo() {
    return this.relationship?.kind === 'belongsTo';
  }

  get relationProperty() {
    const { relationProperty, property } = this.args.column;

    return relationProperty ?? property;
  }

  get relation() {
    return this.loadRelation.value ?? [];
  }

  get relationship() {
    let fields = this.store.schema.fields({
      type: getRecordType(this.args.record),
    });
    let property = this.relationProperty;

    return fields.get(property);
  }

  // Functions
  loadRelation = trackedFunction(this, async () => {
    return await this.args.record[this.relationProperty];
  });

  toggleShowMore = () => {
    this.showMore = !this.showMore;
  };

  getRelation = (relation, column) => {
    if (column.relationPath) {
      return relation[column.relationPath];
    }

    return relation;
  };

  getRelationLabel = (relation, column) => {
    const label = column.relationLabel ?? 'displayLabel';
    return relation.get(label);
  };

  // Template
  <template>
    {{#if this.relation}}
      <div class={{bem (hash is-show-more=this.showMore)}}>
        {{#if this.isBelongsTo}}
          <LinkToDetail
            @model={{this.getRelation this.relation @column}}
            @isDisabled={{@column.isLinkDisabled}}
          >
            {{this.getRelationLabel this.relation @column}}
          </LinkToDetail>
        {{else}}
          {{#if (gt this.relation.length 1)}}
            <div
              class={{bem 'more-count'}}
              role='button'
              {{on 'click' this.toggleShowMore}}
            >
              {{#if this.showMore}}
                {{svgJar 'close' class='u-icon'}}
              {{else}}
                {{t
                  'tables.cells.relation.more'
                  count=(plus this.relation.length -1)
                }}
              {{/if}}
            </div>
          {{/if}}

          <div
            class={{bem
              'first-record'
              (hash has-show-more=(gt this.relation.length 1))
            }}
          >
            <LinkToDetail
              @model={{this.getRelation (get this.relation 0) @column}}
              @isDisabled={{@column.isLinkDisabled}}
            >
              {{this.getRelationLabel (get this.relation 0) @column}}
            </LinkToDetail>
          </div>

          <div
            class={{bem 'all-records'}}
            {{onClickOutside (if this.showMore this.toggleShowMore null)}}
          >
            {{#each this.relation as |record|}}
              <div class={{bem 'record'}}>
                <LinkToDetail
                  @model={{this.getRelation record @column}}
                  @isDisabled={{@column.isLinkDisabled}}
                >
                  {{this.getRelationLabel record @column}}
                </LinkToDetail>
              </div>
            {{/each}}
          </div>
        {{/if}}
      </div>
    {{/if}}
  </template>
}
